<template>
  <div class="agreement-management">
    <div class="agreement-toolbar">
      <div class="agreement-toolbar-left">
        <span class="agreement-toolbar-note">
          Currently only <strong>Minimum Spend v4</strong> is shown here. This card summarizes the <strong>active</strong> template only, use <strong>View all versions</strong> for full history.
        </span>
      </div>
    </div>

    <div class="agreement-type-list">
      <card
        v-for="section in sections"
        :key="section.key"
        class="active__default__module agreement-type-card"
      >
        <p class="agreement-type-card__title">{{ section.title }}</p>
        <div class="agreement-type-card__header-row">
          <!-- <div class="agreement-type-card__meta">
            <el-tag
              v-if="section.activeRow"
              size="mini"
              effect="plain"
              type="success"
              class="agreement-type-card__tag agreement-tag--active"
            >
              Active v{{ section.activeRow.version }}
            </el-tag>
            <el-tag
              v-else
              size="mini"
              effect="plain"
              type="info"
              class="agreement-type-card__tag agreement-tag--muted"
            >
              No active version
            </el-tag>
            <span v-if="section.latestRow" class="agreement-type-card__latest">
              Latest file: v{{ section.latestRow.version }}
            </span>
          </div> -->

          <div class="agreement-card-toolbar">
            <el-button
              type="default"
              plain
              size="small"
              class="agreement-btn-ghost"
              icon="el-icon-document-add"
              @click="openEditorBlank(section.key)"
            >
              New blank version
            </el-button>
            <el-button
              type="default"
              plain
              size="small"
              class="agreement-btn-ghost"
              icon="el-icon-folder-opened"
              @click="openAllVersionsDialog(section)"
            >
              View all versions
            </el-button>
          </div>
        </div>

        <div
          v-if="section.activeRow"
          class="agreement-active-panel"
          v-loading="loading"
        >
          <dl class="agreement-active-dl">
            <div class="agreement-active-dl__row">
              <dt>Version</dt>
              <dd>
                <el-tag size="small" type="info" effect="plain" class="agreement-tag--version">v{{ section.activeRow.version }}</el-tag>
              </dd>
            </div>
            <div class="agreement-active-dl__row">
              <dt>Status</dt>
              <dd>
                <el-tag type="success" effect="plain" size="small" class="agreement-tag--active">Active</el-tag>
              </dd>
            </div>
            <div class="agreement-active-dl__row agreement-active-dl__row--wide">
              <dt>Description</dt>
              <dd>
                <span v-if="section.activeRow.description" class="agreement-desc-text">{{ section.activeRow.description }}</span>
                <span v-else class="agreement-desc-empty">—</span>
              </dd>
            </div>
            <div class="agreement-active-dl__row">
              <dt>Size</dt>
              <dd>
                <el-tooltip
                  v-if="isUnknownFileSize(section.activeRow)"
                  content="Size not available from storage"
                  placement="top"
                >
                  <span class="agreement-size-unknown">—</span>
                </el-tooltip>
                <span v-else class="agreement-cell-body">{{ section.activeRow.file_size_formatted }}</span>
              </dd>
            </div>
            <div class="agreement-active-dl__row">
              <dt>Uploaded by</dt>
              <dd>
                <span v-if="section.activeRow.uploaded_by" class="agreement-cell-body">{{ section.activeRow.uploaded_by.name }}</span>
                <span v-else class="agreement-cell-muted">—</span>
              </dd>
            </div>
            <div class="agreement-active-dl__row">
              <dt>Created</dt>
              <dd class="agreement-cell-body">{{ formatDate(section.activeRow.created_at) }}</dd>
            </div>
            <div class="agreement-active-dl__row">
              <dt>Activated</dt>
              <dd>
                <span v-if="section.activeRow.activated_at" class="agreement-cell-body">{{ formatDate(section.activeRow.activated_at) }}</span>
                <span v-else class="agreement-cell-muted">—</span>
              </dd>
            </div>
          </dl>

          <div class="agreement-active-panel__actions">
            <el-button
              type="primary"
              size="small"
              icon="el-icon-edit"
              class="agreement-btn--primary"
              @click="openEditorFromActive(section.key)"
            >
              Edit active template
            </el-button>
            <div class="agreement-active-panel__icon-actions">
              <el-tooltip content="Create new version starting from this file" placement="top">
                <el-button
                  type="default"
                  plain
                  icon="el-icon-document-copy"
                  size="mini"
                  circle
                  class="agreement-icon-btn"
                  @click="openEditorFromRow(section.activeRow)"
                />
              </el-tooltip>
              <el-tooltip content="Open stored file in new tab" placement="top">
                <el-button
                  type="default"
                  plain
                  icon="el-icon-view"
                  size="mini"
                  circle
                  class="agreement-icon-btn"
                  @click="handlePreview(section.activeRow)"
                />
              </el-tooltip>
              <el-tooltip content="Download HTML" placement="top">
                <el-button
                  type="default"
                  plain
                  icon="el-icon-download"
                  size="mini"
                  circle
                  class="agreement-icon-btn"
                  @click="handleDownload(section.activeRow)"
                />
              </el-tooltip>
            </div>
          </div>
        </div>

        <div v-else class="agreement-active-empty" v-loading="loading">
          <p class="agreement-active-empty__text">
            No active version for this type. Create a version, or open
            <el-button type="text" class="agreement-active-empty__link agreement-link" @click="openAllVersionsDialog(section)">
              all versions
            </el-button>
            to activate one.
          </p>
        </div>
      </card>
    </div>

    <el-dialog
      :title="historyDialogTitle"
      :visible.sync="historyDialogVisible"
      width="1100px"
      append-to-body
      custom-class="agreement-history-dialog"
      @closed="onHistoryDialogClosed"
    >
      <div class="agreement-history-toolbar">
        <el-checkbox v-model="historyIncludeArchived" @change="reloadHistoryData">
          Show archived
        </el-checkbox>
      </div>
      <el-table
        v-if="historySectionForDialog"
          :data="historySectionForDialog.rows"
          v-loading="loading"
          border
          max-height="560"
          class="agreement-table agreement-table--history"
          :row-class-name="agreementRowClassName"
          style="width: 100%"
          empty-text="No versions yet"
        >
        <el-table-column prop="version" label="Version" width="110" align="center">
          <template slot-scope="scope">
            <el-tag size="small" type="info" effect="plain">v{{ scope.row.version }}</el-tag>
          </template>
        </el-table-column>

        <el-table-column prop="status" label="Status" width="108" align="center">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.archived_at" size="small" class="agreement-tag--archived">
              Archived
            </el-tag>
            <el-tag v-else-if="scope.row.status === 'active'" type="success" size="small">
              Active
            </el-tag>
            <el-tooltip v-else-if="!scope.row.archived_at" content="Inactive versions are not shown to end users" placement="top">
              <el-tag size="small" class="agreement-tag--inactive">Inactive</el-tag>
            </el-tooltip>
          </template>
        </el-table-column>

        <el-table-column
          prop="description"
          label="Description"
          min-width="160"
          show-overflow-tooltip
        >
          <template slot-scope="scope">
            <span v-if="scope.row.description" class="agreement-desc-text">{{ scope.row.description }}</span>
            <span v-else class="agreement-desc-empty">—</span>
          </template>
        </el-table-column>

        <el-table-column prop="file_size_formatted" label="Size" width="96" align="right">
          <template slot-scope="scope">
            <el-tooltip
              v-if="isUnknownFileSize(scope.row)"
              content="Size not available from storage"
              placement="top"
            >
              <span class="agreement-size-unknown">—</span>
            </el-tooltip>
            <span v-else class="agreement-cell-body">{{ scope.row.file_size_formatted }}</span>
          </template>
        </el-table-column>

        <el-table-column prop="uploaded_by" label="Uploaded by" min-width="120">
          <template slot-scope="scope">
            <div v-if="scope.row.uploaded_by" class="agreement-cell-body">
              {{ scope.row.uploaded_by.name }}
            </div>
            <div v-else class="agreement-cell-muted">—</div>
          </template>
        </el-table-column>

        <el-table-column prop="created_at" label="Created" min-width="112">
          <template slot-scope="scope">
            <span class="agreement-cell-body">{{ formatDate(scope.row.created_at) }}</span>
          </template>
        </el-table-column>

        <el-table-column prop="activated_at" label="Activated" min-width="112">
          <template slot-scope="scope">
            <div v-if="scope.row.activated_at" class="agreement-cell-body">
              {{ formatDate(scope.row.activated_at) }}
            </div>
            <div v-else class="agreement-cell-muted">—</div>
          </template>
        </el-table-column>

        <el-table-column label="Actions" width="260" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button-group>
              <el-tooltip
                v-if="scope.row.status === 'inactive' && !scope.row.archived_at"
                content="Activate (deactivates other versions of this type)"
                placement="top"
              >
                <el-button
                  size="mini"
                  class="ahd-btn ahd-btn--activate"
                  icon="el-icon-check"
                  @click="handleActivate(scope.row)"
                />
              </el-tooltip>

              <el-tooltip
                v-if="scope.row.status === 'inactive' && !scope.row.archived_at"
                content="Archive this version"
                placement="top"
              >
                <el-button
                  size="mini"
                  class="ahd-btn ahd-btn--archive"
                  icon="el-icon-folder-remove"
                  @click="handleArchive(scope.row)"
                />
              </el-tooltip>

              <el-tooltip
                v-if="scope.row.archived_at"
                content="Restore archived version"
                placement="top"
              >
                <el-button
                  size="mini"
                  class="ahd-btn ahd-btn--restore"
                  icon="el-icon-refresh"
                  @click="handleRestore(scope.row)"
                />
              </el-tooltip>

              <el-tooltip content="Create new version starting from this file" placement="top">
                <el-button
                  size="mini"
                  class="ahd-btn ahd-btn--clone"
                  icon="el-icon-document-copy"
                  @click="openEditorFromRow(scope.row)"
                />
              </el-tooltip>

              <el-tooltip content="Open stored file in new tab" placement="top">
                <el-button
                  size="mini"
                  class="ahd-btn ahd-btn--view"
                  icon="el-icon-view"
                  @click="handlePreview(scope.row)"
                />
              </el-tooltip>

              <el-tooltip content="Download HTML" placement="top">
                <el-button
                  size="mini"
                  class="ahd-btn ahd-btn--download"
                  icon="el-icon-download"
                  @click="handleDownload(scope.row)"
                />
              </el-tooltip>
            </el-button-group>
          </template>
        </el-table-column>
      </el-table>
      <span slot="footer" class="dialog-footer">
        <el-button class="ahd-close-btn" @click="historyDialogVisible = false">Close</el-button>
      </span>
    </el-dialog>

    <AgreementEditorModal
      :visible.sync="editorVisible"
      :agreement-type="editorAgreementType"
      :clone-from-id="editorCloneFromId"
      @saved="onEditorSaved"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import {
  Table,
  TableColumn,
  Tag,
  Tooltip,
  Button,
  ButtonGroup,
  Checkbox,
  MessageBox,
  Message,
  Dialog,
} from 'element-ui';
import AgreementEditorModal from './components/AgreementEditorModal.vue';

const MANAGED_TYPES = ['minimum_spend_v4'];

const SECTION_META = {
  minimum_spend_v4: { title: 'Agency Minimum Spend' },
  default_agency: { title: 'Agency' },
  default_client: { title: 'Client' },
};

export default {
  name: 'AgreementManagement',

  components: {
    AgreementEditorModal,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Tag.name]: Tag,
    [Tooltip.name]: Tooltip,
    [Button.name]: Button,
    [ButtonGroup.name]: ButtonGroup,
    [Checkbox.name]: Checkbox,
    [Dialog.name]: Dialog,
  },

  data() {
    return {
      editorVisible: false,
      editorAgreementType: '',
      editorCloneFromId: null,
      historyDialogVisible: false,
      historySectionKey: null,
      historyIncludeArchived: false,
    };
  },

  computed: {
    ...mapGetters(['agreements', 'agreementLoading']),
    loading() {
      return this.agreementLoading;
    },

    managedAgreements() {
      const set = new Set(MANAGED_TYPES);
      return (this.agreements || []).filter((a) => a && set.has(a.name));
    },

    sections() {
      return MANAGED_TYPES.map((key) => {
        const rows = this.managedAgreements
          .filter((a) => a.name === key)
          .sort((a, b) => parseInt(b.version, 10) - parseInt(a.version, 10));
        const activeRow = rows.find((r) => r.status === 'active') || null;
        const latestRow = rows[0] || null;
        return {
          key,
          title: SECTION_META[key].title,
          rows,
          activeRow,
          latestRow,
        };
      });
    },

    historySectionForDialog() {
      if (!this.historySectionKey) return null;
      return this.sections.find((s) => s.key === this.historySectionKey) || null;
    },

    historyDialogTitle() {
      const s = this.historySectionForDialog;
      return s ? `All versions — ${s.title}` : '';
    },
  },

  mounted() {
    this.fetchAgreements();
  },

  methods: {
    ...mapActions(['fetchAgreements', 'activateAgreement', 'downloadAgreement', 'archiveAgreement', 'restoreAgreement']),

    async fetchAgreements(filters = {}) {
      try {
        await this.$store.dispatch('fetchAgreements', filters);
      } catch (error) {
        Message.error(error || 'Failed to fetch agreements');
      }
    },

    async onEditorSaved() {
      await this.fetchAgreements();
    },

    openEditorFromActive(sectionKey) {
      const active = this.managedAgreements.find((a) => a.name === sectionKey && a.status === 'active');
      if (!active) {
        this.openEditorBlank(sectionKey);
        return;
      }
      this.openEditorFromRow(active);
    },

    openEditorFromRow(row) {
      if (!row || !row.name) return;
      this.editorAgreementType = row.name;
      this.editorCloneFromId = row.id || null;
      this.editorVisible = true;
    },

    openEditorBlank(sectionKey) {
      this.editorAgreementType = sectionKey;
      this.editorCloneFromId = null;
      this.editorVisible = true;
    },

    openAllVersionsDialog(section) {
      this.historyIncludeArchived = false;
      this.historySectionKey = section.key;
      this.historyDialogVisible = true;
      this.reloadHistoryData();
    },

    onHistoryDialogClosed() {
      this.historySectionKey = null;
      this.historyIncludeArchived = false;
      this.fetchAgreements();
    },

    async reloadHistoryData() {
      if (!this.historySectionKey) return;
      await this.fetchAgreements({
        type: this.historySectionKey,
        include_archived: this.historyIncludeArchived,
      });
    },

    async handleActivate(agreement) {
      try {
        await MessageBox.confirm(
          `Activate v${agreement.version}? Other versions of this type will become inactive.`,
          'Confirm activation',
          {
            confirmButtonText: 'Activate',
            cancelButtonText: 'Cancel',
            type: 'warning',
          }
        );

        await this.activateAgreement(agreement.id);
        Message.success('Agreement activated successfully');
        await this.fetchAgreements();
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
          Message.error(error || 'Failed to activate agreement');
        }
      }
    },

    handlePreview(agreement) {
      window.open(agreement.url, '_blank');
    },

    async handleDownload(agreement) {
      try {
        await this.downloadAgreement(agreement.id);
        Message.success('Download started');
      } catch (error) {
        Message.error(error || 'Failed to download agreement');
      }
    },

    async handleArchive(agreement) {
      try {
        await MessageBox.confirm(
          `Archive v${agreement.version}? You can restore it later from archived list.`,
          'Confirm archive',
          {
            confirmButtonText: 'Archive',
            cancelButtonText: 'Cancel',
            type: 'warning',
          }
        );
        await this.archiveAgreement(agreement.id);
        Message.success('Agreement archived successfully');
        await this.reloadHistoryData();
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
          Message.error(error || 'Failed to archive agreement');
        }
      }
    },

    async handleRestore(agreement) {
      try {
        await this.restoreAgreement(agreement.id);
        Message.success('Agreement restored successfully');
        await this.reloadHistoryData();
      } catch (error) {
        Message.error(error || 'Failed to restore agreement');
      }
    },

    agreementRowClassName({ row }) {
      if (row && row.archived_at) return 'agreement-row--archived';
      if (row && row.status === 'active') return 'agreement-row--active';
      return '';
    },

    isUnknownFileSize(row) {
      const s = row && row.file_size_formatted;
      if (s == null || s === '') return true;
      const t = String(s).trim().toLowerCase();
      return t === '0 b' || t === '0b' || t === '0 bytes' || t === 'n/a';
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    },
  },
};
</script>

<style scoped>
.agreement-management {
  padding: 0;
}

.agreement-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.agreement-toolbar-left {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 640px;
}

.agreement-toolbar-note {
  font-size: 12px;
  color: var(--text-primary-color, #606266);
  opacity: 0.85;
  line-height: 1.45;
}

.agreement-management >>> .agreement-btn--primary.el-button--primary {
  background-color: var(--btn-primary-color, #409eff) !important;
  border-color: var(--btn-primary-color, #409eff) !important;
  color: #fff !important;
}

.agreement-management >>> .agreement-btn--primary.el-button--primary:hover,
.agreement-management >>> .agreement-btn--primary.el-button--primary:focus {
  background-color: var(--btn-primary-hover-color, var(--btn-primary-color, #66b1ff)) !important;
  border-color: var(--btn-primary-hover-color, var(--btn-primary-color, #66b1ff)) !important;
  color: #fff !important;
}

.agreement-management >>> .agreement-btn-ghost.el-button--default.is-plain {
  background: transparent !important;
  border-color: var(--input-border-color, #dcdfe6) !important;
  color: var(--text-primary-color, #606266) !important;
}

.agreement-management >>> .agreement-btn-ghost.el-button--default.is-plain:hover,
.agreement-management >>> .agreement-btn-ghost.el-button--default.is-plain:focus {
  border-color: var(--btn-primary-color, #409eff) !important;
  color: var(--btn-primary-color, #409eff) !important;
  background: transparent !important;
}

/* Active status: explicit green (readable on dark + light) */
.agreement-management >>> .agreement-tag--active.el-tag--plain.el-tag--success {
  background-color: rgba(103, 194, 58, 0.15) !important;
  border-color: #67c23a !important;
  color: #67c23a !important;
  display: inline-flex !important;
  justify-content: center !important;
  align-items: center !important;
  min-width: 72px !important;
  height: 24px !important;
  line-height: 1 !important;
  padding: 0 10px !important;
  font-size: 12px !important;
  border-radius: 12px !important;
}

.agreement-management >>> .agreement-tag--muted.el-tag--plain.el-tag--info {
  background-color: var(--muted, #f4f4f5) !important;
  border-color: var(--secondary-border-card, #EBEEF5) !important;
  color: var(--btn-primary-color, #909399) !important;
  display: inline-flex !important;
  justify-content: center !important;
  align-items: center !important;
  min-width: 72px !important;
  height: 24px !important;
  line-height: 1 !important;
  padding: 0 10px !important;
  font-size: 12px !important;
  border-radius: 12px !important;
}

.agreement-management >>> .agreement-tag--version.el-tag--plain.el-tag--info {
  background-color: var(--muted, #f4f4f5) !important;
  border-color: var(--secondary-border-card, #EBEEF5) !important;
  color: var(--btn-primary-color, #606266) !important;
  display: inline-flex !important;
  justify-content: center !important;
  align-items: center !important;
  min-width: 72px !important;
  height: 24px !important;
  line-height: 1 !important;
  padding: 0 10px !important;
  font-size: 12px !important;
  border-radius: 12px !important;
}

.agreement-management >>> .agreement-icon-btn.el-button.is-plain {
  background: transparent !important;
  border-color: var(--input-border-color, #dcdfe6) !important;
  color: var(--text-primary-color, #606266) !important;
}

.agreement-management >>> .agreement-icon-btn.el-button.is-plain:hover,
.agreement-management >>> .agreement-icon-btn.el-button.is-plain:focus {
  border-color: var(--btn-primary-color, #409eff) !important;
  color: var(--btn-primary-color, #409eff) !important;
}

.agreement-management >>> .agreement-link.el-button--text {
  color: var(--btn-primary-color, #409eff) !important;
}

.agreement-management >>> .agreement-link.el-button--text:hover {
  color: var(--btn-primary-hover-color, var(--btn-primary-color, #66b1ff)) !important;
}

.agreement-type-list {
  margin-top: 8px;
}

.agreement-type-card.card {
  margin-bottom: 24px;
}

.agreement-type-card.card.active__default__module {
  border: 0px solid var(--input-border-color, #dcdfe6);
  border-radius: 8px;
}

.agreement-management >>> .agreement-type-card .card-body {
  background: transparent !important;
}

.agreement-type-card__title {
  font-weight: bold;
  text-align: left;
  margin-bottom: 12px;
  color: var(--text-primary-color, #303133);
}

.agreement-type-card__header-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.agreement-type-card__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 0;
}

.agreement-type-card__tag {
  vertical-align: middle;
}

.agreement-type-card__latest {
  font-size: 12px;
  font-weight: normal;
  color: var(--text-primary-color, #909399);
  opacity: 0.8;
}

.agreement-card-toolbar {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  margin: 0;
}

.agreement-active-panel {
  border: 1px solid var(--input-border-color, #ebeef5);
  border-radius: 8px;
  padding: 16px;
  background: rgba(0, 0, 0, 0.04);
}

.agreement-active-dl {
  margin: 0 0 16px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px 24px;
}

.agreement-active-dl__row {
  margin: 0;
}

.agreement-active-dl__row--wide {
  grid-column: 1 / -1;
}

.agreement-active-dl dt {
  margin: 0 0 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: var(--text-primary-color, #909399);
  opacity: 0.9;
}

.agreement-active-dl dd {
  margin: 0;
  font-size: 14px;
  color: var(--text-primary-color, #303133);
  word-break: break-word;
}

.agreement-active-panel__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--input-border-color, #ebeef5);
}

.agreement-active-panel__icon-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.agreement-active-empty {
  border: 1px dashed var(--input-border-color, #dcdfe6);
  border-radius: 8px;
  padding: 20px 16px;
  text-align: center;
}

.agreement-active-empty__text {
  margin: 0;
  font-size: 13px;
  color: var(--text-primary-color, #606266);
  line-height: 1.5;
}

.agreement-active-empty__link {
  padding: 0 4px !important;
  vertical-align: baseline;
}

.agreement-type-cell {
  font-weight: 500;
  color: var(--text-primary-color, #303133);
}

.agreement-cell-body,
.agreement-desc-text {
  color: var(--text-primary-color, #303133);
}

.agreement-desc-empty,
.agreement-cell-muted,
.agreement-size-unknown {
  color: var(--text-primary-color, #909399);
  opacity: 0.75;
}

.agreement-management >>> .agreement-table.el-table {
  background: transparent;
}

.agreement-management >>> .agreement-table.el-table th.el-table__cell {
  background: transparent;
  color: var(--text-primary-color, #303133);
}

.agreement-management >>> .agreement-table.el-table .el-table__body-wrapper tbody td .cell {
  color: var(--text-primary-color, #303133);
}

.agreement-management >>> .agreement-tag--inactive.el-tag {
  background-color: rgba(128, 128, 128, 0.15);
  border-color: var(--input-border-color, #dcdfe6);
  color: var(--text-primary-color, #606266);
}

.agreement-management >>> .agreement-tag--archived.el-tag {
  background-color: rgba(230, 162, 60, 0.15);
  border-color: #e6a23c;
  color: #e6a23c;
}

/* Dark mode only: make panels closer to light mode framing */
:root.dark-theme .agreement-management .agreement-type-card.card.active__default__module {
  border: 1px solid rgba(255, 255, 255, 0.12) !important;
  border-radius: 8px;
}

:root.dark-theme .agreement-management .agreement-active-panel {
  border: 1px solid rgba(255, 255, 255, 0.14) !important;
  background: rgba(255, 255, 255, 0.02) !important;
}

:root.dark-theme .agreement-management .agreement-active-panel__actions {
  border-top: 1px solid rgba(255, 255, 255, 0.14) !important;
}

</style>

<!-- Unscoped: dialog is append-to-body, uses project CSS vars for dark/light -->
<style>
.agreement-history-dialog {
  border-radius: 12px !important;
  overflow: hidden;
}

.agreement-history-dialog .el-dialog {
  border-radius: 12px !important;
  background: var(--background-color-secondary, #ffffff) !important;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18) !important;
}

.agreement-history-dialog .el-dialog__header {
  background: var(--muted, #f4f4f5) !important;
  padding: 16px 24px !important;
  border-bottom: 1px solid var(--secondary-border-card, #EBEEF5);
}

.agreement-history-dialog .el-dialog__title {
  color: var(--text-primary-color, #101111) !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  letter-spacing: 0.01em;
}

.agreement-history-dialog .el-dialog__headerbtn .el-dialog__close {
  color: var(--btn-primary-color, #606266) !important;
  font-size: 18px !important;
}

.agreement-history-dialog .el-dialog__headerbtn .el-dialog__close:hover {
  color: var(--text-primary-color, #303133) !important;
}

.agreement-history-dialog .el-dialog__body {
  background: var(--background-color-secondary, #ffffff) !important;
  padding: 20px 24px !important;
}

.agreement-history-toolbar {
  display: flex;
  justify-content: flex-end;
  margin: 0 0 10px;
}

.agreement-history-dialog .el-dialog__footer {
  background: var(--background-color-secondary, #ffffff) !important;
  border-top: 1px solid var(--secondary-border-card, #EBEEF5);
  padding: 14px 24px !important;
}

/* ── Table ── */
.agreement-history-dialog .el-table {
  background: transparent !important;
  color: var(--text-primary-color, #101111) !important;
}

.agreement-history-dialog .el-table::before,
.agreement-history-dialog .el-table--border::after {
  background-color: var(--secondary-border-card, #EBEEF5) !important;
}

.agreement-history-dialog .el-table th.el-table__cell {
  background: var(--muted, #f4f4f5) !important;
  color: var(--btn-primary-color, #606266) !important;
  border-bottom-color: var(--secondary-border-card, #EBEEF5) !important;
  font-size: 11px !important;
  font-weight: 600 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.04em !important;
}

.agreement-history-dialog .el-table td.el-table__cell {
  border-bottom-color: var(--secondary-border-card, #EBEEF5) !important;
}

.agreement-history-dialog .el-table .el-table__body-wrapper {
  background: transparent !important;
}

.agreement-history-dialog .el-table tr {
  background: transparent !important;
}

.agreement-history-dialog .el-table--enable-row-hover .el-table__body tr:hover > td.el-table__cell {
  background: var(--table-hover-color, #F5F7FA) !important;
}

.agreement-history-dialog .el-table .cell {
  color: var(--text-primary-color, #101111) !important;
  font-size: 13px !important;
}

.agreement-history-dialog .el-table--border td.el-table__cell,
.agreement-history-dialog .el-table--border th.el-table__cell {
  border-right-color: var(--secondary-border-card, #EBEEF5) !important;
}

/* ── Fixed columns ── */
.agreement-history-dialog .el-table__fixed-right {
  background: transparent !important;
}

.agreement-history-dialog .el-table__fixed-right::before {
  background-color: var(--secondary-border-card, #EBEEF5) !important;
}

.agreement-history-dialog .el-table__fixed-right .el-table__fixed-header-wrapper th {
  background: var(--muted, #f4f4f5) !important;
}

.agreement-history-dialog .el-table__fixed-right .el-table__fixed-body-wrapper td {
  background: var(--background-color-secondary, #ffffff) !important;
}

.agreement-history-dialog .el-table--enable-row-hover .el-table__fixed-right .el-table__fixed-body-wrapper tr:hover > td {
  background: var(--table-hover-color, #F5F7FA) !important;
}

/* ── Tags (semantic colors, centered text, readable on both themes) ── */
.agreement-history-dialog .el-tag {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  min-width: 72px !important;
  text-align: center !important;
  line-height: 1 !important;
  padding: 0 10px !important;
  height: 24px !important;
  font-size: 12px !important;
  border-radius: 12px !important;
}

.agreement-history-dialog .el-tag.el-tag--success {
  background: rgba(103, 194, 58, 0.15) !important;
  border-color: #67c23a !important;
  color: #67c23a !important;
}

.agreement-history-dialog .el-tag.el-tag--info {
  background: var(--muted, #f4f4f5) !important;
  border-color: var(--secondary-border-card, #EBEEF5) !important;
  color: var(--btn-primary-color, #909399) !important;
}

.agreement-history-dialog .agreement-tag--inactive.el-tag {
  background: var(--muted, #f4f4f5) !important;
  border-color: var(--secondary-border-card, #EBEEF5) !important;
  color: var(--btn-primary-color, #909399) !important;
}

/* ── Text helpers ── */
.agreement-history-dialog .agreement-desc-text {
  color: var(--text-primary-color, #101111) !important;
}

.agreement-history-dialog .agreement-desc-empty,
.agreement-history-dialog .agreement-cell-muted,
.agreement-history-dialog .agreement-size-unknown {
  color: var(--btn-primary-color, #909399) !important;
}

.agreement-history-dialog .agreement-cell-body {
  color: var(--text-primary-color, #101111) !important;
}

.agreement-history-dialog .el-table__empty-text {
  color: var(--btn-primary-color, #909399) !important;
}

/* ── Action buttons (neutral base) ── */
.agreement-history-dialog .ahd-btn {
  border: 1px solid var(--secondary-border-card, #EBEEF5) !important;
  background: transparent !important;
  color: var(--btn-primary-color, #606266) !important;
  border-radius: 6px !important;
  transition: all 0.15s ease !important;
}

.agreement-history-dialog .ahd-btn:hover {
  border-color: var(--input-border-color, #dcdfe6) !important;
  color: var(--text-primary-color, #303133) !important;
  background: var(--table-hover-color, #F5F7FA) !important;
}

/* Activate: green semantic */
.agreement-history-dialog .ahd-btn--activate {
  border-color: rgba(103, 194, 58, 0.5) !important;
  color: #67c23a !important;
}

.agreement-history-dialog .ahd-btn--activate:hover {
  background: rgba(103, 194, 58, 0.1) !important;
  border-color: #67c23a !important;
  color: #67c23a !important;
}

/* View: blue semantic */
.agreement-history-dialog .ahd-btn--view {
  border-color: rgba(64, 158, 255, 0.5) !important;
  color: #409eff !important;
}

.agreement-history-dialog .ahd-btn--view:hover {
  background: rgba(64, 158, 255, 0.1) !important;
  border-color: #409eff !important;
  color: #409eff !important;
}

/* Download: neutral */
.agreement-history-dialog .ahd-btn--download {
  border-color: var(--secondary-border-card, #EBEEF5) !important;
  color: var(--btn-primary-color, #909399) !important;
}

.agreement-history-dialog .ahd-btn--download:hover {
  background: var(--table-hover-color, #F5F7FA) !important;
  border-color: var(--input-border-color, #c0c4cc) !important;
  color: var(--text-primary-color, #606266) !important;
}

/* Clone: follow primary theme color */
.agreement-history-dialog .ahd-btn--clone {
  border-color: rgba(64, 158, 255, 0.3) !important;
  color: var(--btn-primary-color, #409eff) !important;
}

.agreement-history-dialog .ahd-btn--archive {
  border-color: rgba(230, 162, 60, 0.5) !important;
  color: #e6a23c !important;
}

.agreement-history-dialog .ahd-btn--archive:hover {
  background: rgba(230, 162, 60, 0.1) !important;
  border-color: #e6a23c !important;
  color: #e6a23c !important;
}

.agreement-history-dialog .ahd-btn--restore {
  border-color: rgba(103, 194, 58, 0.5) !important;
  color: #67c23a !important;
}

.agreement-history-dialog .ahd-btn--restore:hover {
  background: rgba(103, 194, 58, 0.1) !important;
  border-color: #67c23a !important;
  color: #67c23a !important;
}

/* ── Close button ── */
.agreement-history-dialog .ahd-close-btn {
  background: var(--muted, #f4f4f5) !important;
  border: 1px solid var(--secondary-border-card, #EBEEF5) !important;
  color: var(--text-primary-color, #101111) !important;
  border-radius: 8px !important;
  padding: 8px 24px !important;
}

.agreement-history-dialog .ahd-close-btn:hover {
  background: var(--table-hover-color, #F5F7FA) !important;
  border-color: var(--input-border-color, #dcdfe6) !important;
  color: var(--text-primary-color, #101111) !important;
}

/* ── Scrollbar ── */
.agreement-history-dialog .el-table .el-table__body-wrapper::-webkit-scrollbar {
  height: 6px;
  width: 6px;
}

.agreement-history-dialog .el-table .el-table__body-wrapper::-webkit-scrollbar-track {
  background: var(--muted, #f4f4f5);
  border-radius: 3px;
}

.agreement-history-dialog .el-table .el-table__body-wrapper::-webkit-scrollbar-thumb {
  background: var(--secondary-border-card, #c0c4cc);
  border-radius: 3px;
}

.agreement-history-dialog .el-table .el-table__body-wrapper::-webkit-scrollbar-thumb:hover {
  background: var(--btn-primary-color, #909399);
}

/* ── Active row highlight ── */
.agreement-history-dialog .agreement-row--active > td {
  background: rgba(103, 194, 58, 0.06) !important;
}

.agreement-history-dialog .agreement-row--archived > td {
  background: rgba(230, 162, 60, 0.06) !important;
}
</style>
